// AuthContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser } from '../services/AuthService';
import type { UserDTO } from '../services/AuthService';
import { postJson } from '../services/MyFetch';
import { clearLocalStorageTokens, hasLoginTokenInUrl, isJwtTokenExpired, saveLoginTokens } from './LoginTokens';
import { globalLogout, globalSetUser } from './ProtectedRoute';
import { useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: UserDTO | null;
  login: (user: UserDTO) => void;
  logout: () => void;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState<UserDTO>(null);
  const [loading, setLoading] = useState(true);
  let refreshInterval = null;

  useEffect(() => {
    console.log('AuthContext');
    const fetchUser = async () => {

      if (hasLoginTokenInUrl()) {
        clearLocalStorageTokens();
        saveLoginTokens();
        setLoading(false);
        return;
      }

      var token = localStorage.getItem('token');
      if (token) {
        if (isJwtTokenExpired(token)) {
          console.log('jwt token expired');
          setLoading(false);
          clearLocalStorageTokens();
          return;
        }
        try {
          setLoading(true);
          var resp = await getCurrentUser();
          globalSetUser(resp);
          setUser(resp);
        } catch (error) {
          console.error('Error fetching user:', error);
          setUser(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const login = (userData:UserDTO) => {
    setUser(userData); 
    if (refreshInterval) {
      clearInterval(refreshInterval);
    }
    refreshInterval = setInterval(refreshToken, 5 * 60 * 1000);
  };
  
  const refreshToken = async () => {
    try {
      let token = localStorage.getItem('refreshToken');
      const { jwt } = await postJson('/api/refreshToken', {refreshToken:token});
      localStorage.setItem('token', jwt)
    } catch (error) {
      console.error('Refresh token failed:', error);
      logout();
    }
  }

  const logout = () => {
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = null;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null); 
  };


  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);